import { useState } from "react";
import { Lightbulb, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { useFetch, lastMonth } from "../lib/api";
import { PageHeader, Pill } from "../components/shared";
import { TrendChart, Change } from "../components/ScoreTrend";
import { AiSummary } from "../components/AiSummary";
import { useMember } from "../lib/member";
import { MonthPicker } from "../components/DateField";
import { HealthReport } from "../components/HealthReport";

const GRADE = { A: "green", B: "blue", C: "amber", D: "red" };

const Part = ({ b }) => (
  <div className="grid grid-cols-[1fr_60px] gap-3 py-2.5 border-b border-[#1F2A3E]/60 last:border-0" data-testid={`my-score-part-${b.key}`}>
    <div><div className="text-sm">{b.label}</div><div className={`text-xs ${b.points < b.max ? "text-rose-300" : "text-slate-500"}`}>{b.note}</div>
      {b.tip && <div className="mt-1.5 flex gap-1.5 rounded-md border border-[#00E599]/20 bg-[#00E599]/5 px-2 py-1.5 text-xs text-emerald-100" data-testid={`my-score-tip-${b.key}`}>
        <Lightbulb size={13} className="mt-0.5 shrink-0 text-[#00E599]" /><span>{b.tip}</span></div>}</div>
    <div className={`font-mono text-right ${b.points < b.max ? "text-rose-300" : "text-emerald-300"}`}>{b.points}/{b.max}</div>
  </div>);

export default function MyScore() {
  const [month, setMonth] = useState(lastMonth());
  const { me } = useMember();
  const { data } = useFetch(`/pe/my-scorecard?month=${month}`);
  return (
    <div data-testid="my-score-page">
      <PageHeader eyebrow="Contractor" title="My compliance score" desc="Your monthly score out of 100 from the principal employer, and where points were lost. PF on time 30, ESIC on time 25, PF challan matches ECR 20, ESIC challan matches ESIC file 10, no payment hold 15. Late filing earns half. Penalties: up to −20 when workers are paid below minimum wage (by share of workers, at least −5), −15 while the CLRA licence is expired.">
        <MonthPicker value={month} onChange={setMonth} testid="my-score-month" className="w-44" />
      </PageHeader>
      <HealthReport />
      {me?.vendor_id && <div className="mb-6 -mt-4"><AiSummary vid={me.vendor_id} month={month} /></div>}
      {!data ? <p className="text-xs text-slate-500 font-mono">Loading…</p> : (
        <div className="grid lg:grid-cols-[340px_1fr] gap-6">
          <div className="c-surface p-6 c-rise">
            <div className="c-eyebrow">{data.month}</div>
            <div className="flex items-baseline gap-3 mt-3">
              <span className="font-display text-6xl font-semibold" data-testid="my-score-page-value">{data.score ?? "—"}</span>
              {data.grade && <Pill tone={GRADE[data.grade]} testid="my-score-page-grade">Grade {data.grade}</Pill>}
            </div>
            {data.in_progress && <p className="text-xs text-slate-500 mt-2">In progress — filings for this month are not due yet.</p>}
            {data.score == null && <p className="text-xs text-slate-500 mt-2">No wages entered for this month, so there is nothing to score.</p>}
            <div className="text-xs text-slate-400 mt-4">6-month average <span className="font-mono text-white" data-testid="my-score-avg6">{data.average_6m ?? "—"}</span></div>
            {data.raises_unpaid > 0 && <Link to="/raises" className="mt-4 flex gap-1.5 rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-1.5 text-xs text-rose-200" data-testid="my-score-raises-unpaid">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" /><span>{data.raises_unpaid} worker(s) still not confirmed as paid the new minimum wage. Open Wage Raises to tick them as paid with proof.</span></Link>}
            <div className="mt-5">{data.breakdown.map((b) => <Part key={b.key} b={b} />)}
              {(data.penalties || []).map((p) => (
                <div key={p.key} className="grid grid-cols-[1fr_60px] gap-3 py-2.5 border-b border-[#1F2A3E]/60 last:border-0" data-testid={`my-score-penalty-${p.key}`}>
                  <div><div className="text-sm text-rose-200">Penalty · {p.label}</div><div className="text-xs text-rose-300">{p.note}</div>
                    <div className="mt-1.5 flex gap-1.5 rounded-md border border-[#00E599]/20 bg-[#00E599]/5 px-2 py-1.5 text-xs text-emerald-100"><Lightbulb size={13} className="mt-0.5 shrink-0 text-[#00E599]" /><span>{p.tip}</span></div></div>
                  <div className="font-mono text-right text-rose-300">−{p.points}</div></div>))}</div>
          </div>
          <div className="c-surface p-6 c-rise grid gap-3 content-start">
            <div className="flex flex-wrap items-center justify-between gap-3"><div className="c-eyebrow">12-month trend</div><Change d={data} /></div>
            <TrendChart trend={data.trend} />
          </div>
        </div>)}
    </div>);
}
